import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, CheckCircle2, Plus, Send, MessageSquare, ChevronDown, ThumbsUp, ThumbsDown } from "lucide-react";
import { api } from "@/lib/api";
import { PageHead, Loading } from "@/components/common";

const STATUS = {
  open: { label: "Open", bg: "var(--gold-soft)", color: "var(--gold-2)" },
  answered: { label: "Answered", bg: "rgba(16,185,129,.14)", color: "var(--success, #059669)" },
  resolved: { label: "Resolved", bg: "var(--surface-3)", color: "var(--muted)" },
};

function StatusChip({ status }) {
  const s = STATUS[status] || STATUS.open;
  return <span data-testid={`ticket-status-${status}`} style={{ fontSize: 11, fontWeight: 800, padding: "3px 10px", borderRadius: 999, background: s.bg, color: s.color, textTransform: "uppercase", letterSpacing: 0.4 }}>{s.label}</span>;
}

function fmt(ts) {
  try { return new Date(ts).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
}

function Thread({ ticket, onUpdated }) {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const items = [
    { from: "user", message: ticket.message, at: ticket.created_at },
    ...(ticket.replies || []).map((r) => ({ from: r.from === "admin" || r.admin_id ? "admin" : "user", message: r.message, at: r.at, name: r.admin_name || r.name })),
  ];
  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [ticket.replies?.length]);

  const send = async () => {
    const text = reply.trim();
    if (text.length < 2) return toast.error("Type a message first");
    setBusy(true);
    try {
      const res = await api.post(`/support/${ticket.id}/reply`, { message: text });
      setReply("");
      onUpdated(res.ticket);
      toast.success("Reply sent");
    } catch (e) { toast.error(e.message || "Could not send"); }
    finally { setBusy(false); }
  };

  const hasAdminReply = (ticket.replies || []).some((r) => r.from === "admin" || r.admin_id);
  const resolve = async () => {
    try { const res = await api.post(`/support/${ticket.id}/resolve`); onUpdated(res.ticket); toast.success("Marked as resolved"); }
    catch (e) { toast.error(e.message || "Could not update"); }
  };
  const rate = async (rating) => {
    try { const res = await api.post(`/support/${ticket.id}/rate`, { rating }); onUpdated(res.ticket); toast.success("Thanks for the feedback!"); }
    catch (e) { toast.error(e.message || "Could not save rating"); }
  };

  return (
    <div data-testid={`ticket-thread-${ticket.id}`}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
        {items.map((m, i) => {
          const admin = m.from === "admin";
          return (
            <div key={i} style={{ display: "flex", justifyContent: admin ? "flex-start" : "flex-end" }}>
              <div style={{ maxWidth: "82%", padding: "10px 14px", borderRadius: 14, fontSize: 14, lineHeight: 1.6, whiteSpace: "pre-wrap",
                background: admin ? "var(--surface-3)" : "var(--brand)", color: admin ? "var(--text)" : "#fff",
                borderTopLeftRadius: admin ? 4 : 14, borderTopRightRadius: admin ? 14 : 4 }}>
                <div style={{ fontSize: 11, fontWeight: 800, opacity: 0.75, marginBottom: 3 }}>{admin ? (m.name || "SouqStream Support") : "You"} · {fmt(m.at)}</div>
                {m.message}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {hasAdminReply && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "12px 14px", background: "var(--gold-soft)", borderRadius: 12, marginBottom: 14 }} data-testid={`ticket-actions-${ticket.id}`}>
          {ticket.rating ? (
            <span style={{ fontSize: 13, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 6 }} data-testid={`ticket-rated-${ticket.id}`}>
              {ticket.rating === "up" ? <ThumbsUp size={15} style={{ color: "var(--success,#059669)" }} /> : <ThumbsDown size={15} style={{ color: "var(--danger,#dc2626)" }} />}
              Thanks for rating this {ticket.rating === "up" ? "helpful" : "not helpful"}.
            </span>
          ) : (
            <>
              <span style={{ fontSize: 13, fontWeight: 700 }}>Was this helpful?</span>
              <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => rate("up")} data-testid={`ticket-rate-up-${ticket.id}`}><ThumbsUp size={15} /> Yes</button>
              <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => rate("down")} data-testid={`ticket-rate-down-${ticket.id}`}><ThumbsDown size={15} /> No</button>
            </>
          )}
          {ticket.status !== "resolved" && (
            <button className="ss-btn ss-btn-gold ss-btn-sm" style={{ marginLeft: "auto" }} onClick={resolve} data-testid={`ticket-resolve-${ticket.id}`}><CheckCircle2 size={15} /> Mark resolved</button>
          )}
        </div>
      )}

      {ticket.status === "resolved" ? (
        <div className="ss-muted" style={{ fontSize: 13, textAlign: "center", padding: "8px 0" }}>This ticket is resolved. Send a message to reopen it.</div>
      ) : null}
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
        <div className="ss-field" style={{ flex: 1, height: "auto", padding: 10, alignItems: "flex-start" }}>
          <textarea rows={2} placeholder="Write a reply…" value={reply} onChange={(e) => setReply(e.target.value)}
            style={{ width: "100%", border: "none", outline: "none", background: "transparent", resize: "vertical", fontFamily: "var(--font-body)", fontSize: 14, color: "var(--text)" }}
            data-testid={`ticket-reply-input-${ticket.id}`} />
        </div>
        <button className="ss-btn ss-btn-primary" onClick={send} disabled={busy} data-testid={`ticket-reply-send-${ticket.id}`} style={{ height: 46 }}><Send size={15} /> {busy ? "Sending…" : "Send"}</button>
      </div>
    </div>
  );
}

function NewRequest({ subjects, onCreated }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!subject) return toast.error("Choose a subject");
    if (message.trim().length < 10) return toast.error("Please add a few more details (min 10 chars)");
    setSending(true);
    try {
      await api.post("/support/contact", { subject, message: message.trim() });
      setSubject(""); setMessage("");
      toast.success("Message sent — we'll reply soon");
      onCreated();
    } catch (err) { toast.error(err.message || "Could not send"); }
    finally { setSending(false); }
  };
  return (
    <form className="ss-card" style={{ padding: 22, display: "flex", flexDirection: "column", gap: 16 }} onSubmit={submit} data-testid="support-new-form">
      <div>
        <label className="ss-label">Subject</label>
        <select className="ss-field" style={{ width: "100%" }} value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="support-subject">
          <option value="">Select a subject</option>
          {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <label className="ss-label">Message</label>
        <div className="ss-field" style={{ height: "auto", padding: 12, alignItems: "flex-start" }}>
          <textarea rows={4} placeholder="How can we help?" value={message} onChange={(e) => setMessage(e.target.value)} style={{ width: "100%", border: "none", outline: "none", background: "transparent", resize: "vertical", fontFamily: "var(--font-body)", fontSize: 15, color: "var(--text)" }} data-testid="support-message" />
        </div>
      </div>
      <button className="ss-btn ss-btn-primary ss-btn-block" disabled={sending} data-testid="support-submit"><LifeBuoy size={16} /> {sending ? "Sending…" : "Send message"}</button>
    </form>
  );
}

export default function Support() {
  const [subjects, setSubjects] = useState([]);
  const [tickets, setTickets] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [openId, setOpenId] = useState(null);

  const loadTickets = () => api.get("/support/my").then((d) => setTickets(d.tickets || [])).catch(() => setTickets([]));
  useEffect(() => {
    api.get("/support/subjects").then((d) => setSubjects(d.subjects)).catch(() => {});
    loadTickets();
  }, []);

  const onUpdated = (t) => setTickets((ts) => (ts || []).map((x) => x.id === t.id ? t : x));

  if (tickets === null) return <Loading />;
  const hasTickets = tickets.length > 0;

  return (
    <div className="fade-up" style={{ maxWidth: 720, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <PageHead title="Support" sub="Message our team and reply right here — every reply stays in the same thread." />
        <button className="ss-btn ss-btn-primary ss-btn-sm" onClick={() => setShowForm((v) => !v)} data-testid="support-new-toggle"><Plus size={15} /> New request</button>
      </div>

      {(showForm || !hasTickets) && (
        <div style={{ marginBottom: 22 }}>
          <NewRequest subjects={subjects} onCreated={() => { setShowForm(false); loadTickets(); }} />
        </div>
      )}

      {hasTickets && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="support-tickets">
          {tickets.map((t) => {
            const open = openId === t.id;
            const lastReply = (t.replies || [])[(t.replies || []).length - 1];
            const preview = lastReply?.message || t.message;
            return (
              <div key={t.id} className="ss-card" style={{ padding: 0, overflow: "hidden" }} data-testid={`ticket-${t.id}`}>
                <button onClick={() => setOpenId(open ? null : t.id)} data-testid={`ticket-toggle-${t.id}`}
                  style={{ width: "100%", textAlign: "left", background: "transparent", border: "none", cursor: "pointer", padding: 18, display: "flex", alignItems: "center", gap: 12 }}>
                  <div className="stat-ic" style={{ flexShrink: 0 }}><MessageSquare size={18} /></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 800, fontSize: 15 }}>{t.subject}</span>
                      <StatusChip status={t.status} />
                    </div>
                    <div className="ss-muted" style={{ fontSize: 13, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{preview}</div>
                  </div>
                  <ChevronDown size={18} className="ss-muted" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s", flexShrink: 0 }} />
                </button>
                {open && <div style={{ padding: "0 18px 18px" }}><Thread ticket={t} onUpdated={onUpdated} /></div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
